/**
 * Cliente de coordinación del peritaje por WebSocket.
 *
 * Mantiene un único socket a `ws/inspection/<id>/` que sirve tanto para el
 * progreso como para el trabajo colaborativo, y reconcilia los locks del
 * servidor con los formularios que tienen cambios sin guardar en esta sesión.
 *
 * Mientras el socket está caído se consulta `GET /peritaje/collab/<id>/` para
 * no perder presencia ni locks, y se reconecta solo.
 */
import { wsBackend } from '../constants/api';
import { get } from '../services/back.service';
import {
  applyLock,
  applySnapshot,
  clearDenied,
  currentUserId,
  dirtyScopes,
  dropLocalLock,
  getState,
  holdsLock,
  isDenied,
  noteRemoteSave,
  reset as resetStore,
  setConnected,
  setDenied,
  setInspectionId,
  setPresence,
  setVersion,
  subscribe,
} from './store';

const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 60000;
// Código que el backend envía cuando el peritaje no existe: no se reintenta.
const CLOSE_NOT_FOUND = 4404;
const POLL_MS = 10000;
// El lock del servidor dura 90 s; renovarlo cada 30 s deja margen de sobra.
const HEARTBEAT_MS = 30000;
// Evita pedir el mismo lock en bucle cuando el servidor acaba de negarlo.
const ACQUIRE_THROTTLE_MS = 1500;

let socket = null;
let inspectionId = null;
let reconnectTimer = null;
let pollTimer = null;
let heartbeatTimer = null;
let stopped = false;
let subscribers = 0;
let lastAcquireAt = {};
let reconnectAttempts = 0;

function nextReconnectDelay() {
  const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** reconnectAttempts);
  reconnectAttempts += 1;
  return delay;
}

function send(payload) {
  if (socket && socket.readyState === 1) {
    socket.send(JSON.stringify(payload));
  }
}

/**
 * Pide los locks de los formularios con cambios sin guardar y suelta los que ya
 * se guardaron. Se ejecuta tras cada cambio de estado del store.
 */
function reconcile() {
  if (!socket || socket.readyState !== 1) return;

  const me = currentUserId();
  const now = Date.now();
  const { locks, dirty } = getState();

  Object.keys(locks).forEach((scope) => {
    const mine = me === null ? true : locks[scope].holder_id === me;
    if (mine && !dirty[scope]) {
      send({ action: 'lock.release', scope });
    }
  });

  Object.keys(dirty).forEach((scope) => {
    if (holdsLock(scope)) return;
    if (isDenied(scope)) return;
    if (now - (lastAcquireAt[scope] || 0) < ACQUIRE_THROTTLE_MS) return;
    lastAcquireAt[scope] = now;
    send({ action: 'lock.acquire', scope });
  });
}

function sendHeartbeat() {
  if (!socket || socket.readyState !== 1) return;
  send({ action: 'presence.heartbeat' });
  dirtyScopes().forEach((scope) => {
    if (holdsLock(scope)) send({ action: 'lock.heartbeat', scope });
  });
}

async function refreshFromRest() {
  if (!inspectionId) return;
  const response = await get(`/peritaje/collab/${inspectionId}`, {});
  if (response && response.success && response.data) applySnapshot(response.data);
}

function handleMessage(raw) {
  let message;
  try {
    message = JSON.parse(raw);
  } catch (e) {
    return;
  }

  switch (message.type) {
    case 'collab.state':
      applySnapshot(message.data);
      break;

    case 'collab.presence':
      setPresence(message.presence);
      break;

    case 'collab.lock':
      applyLock(message);
      break;

    case 'collab.denied':
      setDenied(message.scope, message.holder);
      break;

    case 'collab.lost':
      // Perdimos el lock (expiró o alguien lo tomó). Si sigue pendiente, la
      // reconciliación vuelve a pedirlo; si lo tiene otro, el evento de lock
      // que lo acompaña bloquea este formulario.
      dropLocalLock(message.scope);
      break;

    case 'collab.saved': {
      const { scope, version, by } = message;
      if (!scope) break;
      setVersion(scope, version);
      const me = currentUserId();
      const isOwn = by && me !== null ? by.id === me : Boolean(getState().dirty[scope]);
      if (!isOwn) noteRemoteSave(scope);
      break;
    }

    default:
      break;
  }
}

function startPolling() {
  if (stopped || pollTimer) return;
  pollTimer = setInterval(refreshFromRest, POLL_MS);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function connect() {
  if (stopped || !inspectionId) return;

  const token = localStorage.getItem('token') || '';
  const url = `${wsBackend}/ws/inspection/${inspectionId}/?token=${encodeURIComponent(token)}`;

  let down = false;
  socket = new WebSocket(url);

  socket.onopen = () => {
    reconnectAttempts = 0;
    setConnected(true);
    stopPolling();
    send({ action: 'presence.heartbeat' });
    reconcile();
  };

  socket.onmessage = (event) => handleMessage(event.data);

  const handleDown = (event) => {
    if (down || stopped) return;
    down = true;
    setConnected(false);
    socket = null;
    if (event && event.code === CLOSE_NOT_FOUND) {
      stopped = true;
      stopPolling();
      return;
    }
    startPolling();
    refreshFromRest();
    reconnectTimer = setTimeout(connect, nextReconnectDelay());
  };

  socket.onclose = handleDown;
  socket.onerror = handleDown;
}

/**
 * Abre (o reutiliza) la coordinación de un peritaje. Devuelve la función para
 * cerrarla; el socket se mantiene mientras quede algún consumidor.
 */
export function connectCollaboration(targetInspectionId) {
  // Las rutas entregan el id como texto: "0" es truthy, por eso se valida como entero.
  const id = Number(targetInspectionId);
  if (!Number.isInteger(id) || id <= 0) return () => {};

  subscribers += 1;

  if (subscribers === 1) {
    stopped = false;
    lastAcquireAt = {};
    reconnectAttempts = 0;
    inspectionId = id;
    setInspectionId(id);
    connect();
    startPolling();
    heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_MS);
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    subscribers -= 1;
    if (subscribers > 0) return;

    stopped = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
    stopPolling();
    if (socket) socket.close();
    socket = null;
    inspectionId = null;
    resetStore();
  };
}

/** Reactiva un formulario tomado por otra persona (toma de control). */
export function takeOverScope(scope) {
  if (!scope) return;
  clearDenied(scope);
  lastAcquireAt[scope] = 0;
  send({ action: 'lock.acquire', scope, force: true });
}

// Los locks se piden y sueltan en respuesta a los cambios del store.
subscribe(reconcile);